import { io, Socket } from 'socket.io-client';
import { useAuthStore } from '../store/auth';

let socket: Socket | null = null;
let authUnsub: (() => void) | null = null;

export function connectSocket(): Socket | null {
  const isAuthenticated = useAuthStore.getState().isAuthenticated;

  if (!isAuthenticated) {
    // Race condition: connectSocket może zostać wywołany zanim Zustand
    // odtworzy status sesji. Po hydratacji socket uwierzytelnia się cookie.
    // Bez tego socket nigdy sie nie laczy i real-time pushe (message:new
    // z Pub/Sub workera) przepadaja.
    if (!authUnsub) {
      console.warn('[Socket] No active session - waiting for auth store');
      authUnsub = useAuthStore.subscribe((state) => {
        if (state.isAuthenticated && !socket) {
          console.log('[Socket] Auth session detected after rehydration - connecting');
          authUnsub?.();
          authUnsub = null;
          connectSocket();
        }
      });
    }
    return null;
  }

  if (socket?.connected) {
    return socket;
  }

  // If socket exists but disconnected, reuse it (it will reconnect)
  if (socket) {
    return socket;
  }

  socket = io({
    withCredentials: true,
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionAttempts: 5,
    reconnectionDelay: 1000,
  });

  socket.on('connect', () => {
    console.log('Socket connected');
  });

  socket.on('disconnect', (reason) => {
    console.log('Socket disconnected:', reason);
  });

  socket.on('connect_error', (error) => {
    console.error('Socket connection error:', error.message);
  });

  return socket;
}

/**
 * Wait for socket to be connected, then execute callback.
 * If already connected, runs immediately. Re-runs on reconnect too.
 * Jesli socket jest null (sesja jeszcze nie zostala odtworzona), obserwuje auth
 * store i retry-uje gdy sesja sie pojawi - inaczej handlery (message:new
 * itd.) nie zostalyby nigdy podpiete.
 */
export function onSocketReady(callback: (s: Socket) => void | (() => void)): (() => void) | undefined {
  let cleanedUp = false;
  let waitCleanup: (() => void) | null = null;
  let connectionCleanup: (() => void) | null = null;
  let callbackCleanup: (() => void) | null = null;

  const attach = (s: Socket) => {
    if (cleanedUp) return;
    const runCallback = () => {
      callbackCleanup?.();
      callbackCleanup = callback(s) ?? null;
    };
    if (s.connected) runCallback();
    const handler = () => runCallback();
    s.on('connect', handler);
    connectionCleanup = () => s.off('connect', handler);
  };

  const s = connectSocket();
  if (s) {
    attach(s);
  } else {
    // Socket not ready - czekaj aż auth store odtworzy status sesji.
    const unsub = useAuthStore.subscribe((state) => {
      if (!cleanedUp && state.isAuthenticated) {
        unsub();
        const ready = connectSocket();
        if (ready) attach(ready);
      }
    });
    waitCleanup = () => { unsub(); };
  }

  return () => {
    cleanedUp = true;
    waitCleanup?.();
    connectionCleanup?.();
    callbackCleanup?.();
  };
}

export function disconnectSocket(): void {
  if (socket) {
    socket.disconnect();
    socket = null;
  }
}

export function getSocket(): Socket | null {
  return socket;
}

export function subscribeToOrders(): void {
  socket?.emit('subscribe:orders');
}

export function unsubscribeFromOrders(): void {
  socket?.emit('unsubscribe:orders');
}

export function subscribeToStore(storeId: string): void {
  socket?.emit('subscribe:store', storeId);
}

export function unsubscribeFromStore(storeId: string): void {
  socket?.emit('unsubscribe:store', storeId);
}

export function subscribeToLaboratorium(): void {
  socket?.emit('subscribe:laboratorium');
}

export function unsubscribeFromLaboratorium(): void {
  socket?.emit('unsubscribe:laboratorium');
}

export function subscribeToMessages(storeId: string): void {
  socket?.emit('subscribe:messages', storeId);
}

export function unsubscribeFromMessages(storeId: string): void {
  socket?.emit('unsubscribe:messages', storeId);
}
